import { clsx } from 'clsx'
import { type Dispatch, type FC, type SetStateAction, useId } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { type ClientSessionCustomerData } from '@redteclab/api/clients/bully'

import { FormControl, FormHelperText, FormLabel, Option, Select } from 'base-ui'

import { type ContactFormState } from '../../../contact-form'
import { useGlobalConfigContext } from '../../../global-config'
import { type SubOrderProductListProps } from '../../hooks/useDirectContactFormOrderSelection'
import { directContactFormAccessorGetSubjects } from '../../model/directContactFormAccessorGetSubjects'

import { useDirectFormDialogContext } from './DirectContactFormDialogContext'

const handleFormSubjectChange = ({
  formState,
  setProductList,
  setSubject,
  setSubOrderNumber,
  setUserData,
  value,
}: {
  formState: ContactFormState
  setProductList: (value: SubOrderProductListProps[]) => void
  setSubject: (value: string) => void
  setSubOrderNumber: (value: string) => void
  setUserData: Dispatch<SetStateAction<ClientSessionCustomerData>>
  value: string
}): void => {
  formState.setValue(value, formState.model, 'subject')
  setSubject(value)
  setSubOrderNumber('')
  setProductList([])

  const { email, firstName, lastName, phoneNumber } = formState.model

  setUserData({ email, firstName, lastName, phone: phoneNumber ?? '' })
}

export const DirectContactFormSelectSubjectField: FC<{
  formState: ContactFormState
}> = ({ formState }) => {
  const intl = useIntl()
  const { tenant } = useGlobalConfigContext()

  const {
    disableSubject,
    setProductList,
    setSubject,
    setSubOrderNumber,
    setUserData,
  } = useDirectFormDialogContext()

  const subjectId = useId()

  return (
    <FormControl
      className="mb-2"
      disabled={disableSubject}
      error={!formState.model.validator.isPropertyValid('subject')}
      onChange={formState.makeOnChangeHandler(formState.model, 'subject')}
      required
      value={formState.model.subject}
    >
      <FormLabel htmlFor={subjectId}>
        <FormattedMessage id="general.label.subject" />
      </FormLabel>
      <Select
        className={clsx(
          'text-m font-medium',
          formState.model.validator.getFirstErrorFor('subject') &&
            'border border-dark-brand',
        )}
        data-qa-id="contact-form-select-subject"
        onChange={(event, value): void => {
          if (!value) {
            return
          }
          handleFormSubjectChange({
            formState,
            setProductList,
            setSubject,
            setSubOrderNumber,
            setUserData,
            value,
          })
        }}
        slotProps={{
          root: {
            'aria-label': intl.formatMessage({ id: 'general.label.subject' }),
          },
        }}
        value={formState.model.subject}
      >
        {directContactFormAccessorGetSubjects({ intl, tenant }).options.map(
          (option) => {
            return (
              <Option
                data-qa-id="contact-form-select-options"
                key={option}
                value={option}
              >
                <span className="flex w-full justify-start">{option}</span>
              </Option>
            )
          },
        )}
      </Select>
      {formState.model.validator.getFirstErrorFor('subject') && (
        <FormHelperText
          className="text-dark-error"
          data-qa-id="contact-form-subject-error-msg"
        >
          {formState.model.validator.getFirstErrorFor('subject')}
        </FormHelperText>
      )}
    </FormControl>
  )
}
